<template>
  <div>
    <wc-dialog :showDialog='showDialog' title='Work Order Quick View' :icon='$store.state.icons.modules.workorders.icon'
      :loading='isLoading' width='1200' hideCancel saveText='Close' @save="close">
      <template v-slot:toolbarButtons>
        <span v-if='woCopy' class='caption'>
          Work Order created on {{ woCopy.date_created | shortDate }}
        </span>
        <span v-if='woCopy && woCopy.estimate && woCopy.estimate_obj.estimator' class='caption ml-4'>
          Estimator is {{ woCopy.estimate_obj.estimator_obj.name_display }}
        </span>
      </template>
      <div v-if='woCopy'>
        <v-row dense>
          <v-col>
            <span class='headline'>{{ woCopy.project ? woCopy.project_obj.name : woCopy.estimate_obj.name }}</span>
            <span class='ml-4 subtitle-1'>{{ woCopy.project ? woCopy.project_obj.site_address :
              woCopy.estimate_obj.site_address }}</span>
            <span class='ml-4 body-2'>{{ woCopy.project ? woCopy.project_obj.site_city : woCopy.estimate_obj.site_city
              }}</span>
            <wc-is-capital-install small class="ml-4" style='vertical-align: text-bottom;'
              v-if='woCopy.project_obj && woCopy.project_obj.project_detail && !woCopy.project_obj.project_detail.is_capital_system'></wc-is-capital-install>
            <v-tooltip open-delay='500' top v-if='woCopy.is_attempted'>
              <template v-slot:activator='{ on }'>
                <v-chip class='ml-4 mt-n2' v-on='on' v-if='woCopy.is_attempted' small color='warning' dark
                  @click='confirmClear'>Attempted</v-chip>
              </template>
              <span class='caption'>Click here to clear attempted status</span>
            </v-tooltip>
            <v-chip class='ml-4 mt-n2' v-if='woCopy.is_complete' small color='grey' dark>Completed</v-chip>
            <v-chip class='ml-4 mt-n2' v-if='woCopy.on_hold' small color='error' dark>
              <v-icon small left>{{ $icons.hold.icon }}</v-icon> On Hold
            </v-chip>
          </v-col>

          <v-col cols='auto' class='text-right my-auto'>
            <v-tooltip open-delay='500' top>
              <template v-slot:activator='{ on }'>
                <span v-on='on' class='mr-2 text-right body-2 success--text'>{{ wo.minimum_price | intMoney }}</span>
              </template>
              <span class='caption'>Minimum charge for this wo (value used for route total)</span>
            </v-tooltip>
            <v-chip label small :color='woType.color' class='mr-2 white--text'>{{ woType.name }}</v-chip>
            <wo-timeline small left :timeline='woCopy.timeline'></wo-timeline>
            <span class='subtitle-2'>{{ woCopy.number }}</span>
          </v-col>
        </v-row>
        <v-row dense>

          <v-col cols='12' md='4'>
            <wc-data-box mini title='Problem Description' titleIcon='mdi-help-circle-outline' titleIconColor='orange'>
              <v-card-text v-if='woCopy.problem_description' v-html='woCopy.problem_description'></v-card-text>
              <v-card-text v-else>No problem description provided</v-card-text>
            </wc-data-box>

            <!--  proj sched cons will contain both the one time note and perm note-->
            <wc-data-box mini v-if="woCopy.project_scheduling_considerations" title='Project Scheduling Considerations'
              titleIcon='mdi-calendar-cursor' titleIconColor='error' showHelp class='mt-2'
              helpText='These notes are automatically pulled in from the project and can not be edited here'>
              <v-card-text>
                <span style='white-space: pre-wrap' v-html='woCopy.project_scheduling_considerations'></span>
              </v-card-text>
            </wc-data-box>
            <wc-data-box mini title='One Time SU Field Note' titleIcon='mdi-text-subject' titleIconColor='warning'
              v-if='!!suNote.length' class='mt-2'>
              <v-card-text v-html='suNote[0].note'></v-card-text>
            </wc-data-box>
          </v-col>
          <v-col cols='4'>
            <contacts mini v-if='woCopy.project' :project='woCopy.project_obj'></contacts>
            <estimate-contacts v-else :estimate='woCopy.estimate_obj'></estimate-contacts>
          </v-col>
          <v-col cols='12' md='4'>
            <wo-scheduling-considerations ref='schedulingConsiderations' @update='updateWO' v-if='woCopy' :wo='woCopy'
              :isLoading='isLoading' mini schedule></wo-scheduling-considerations>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols='4'>
            <stickies mini v-if='woCopy && (woCopy.project_obj || woCopy.estimate_obj)' :project='woCopy.project_obj'
              :estimate='woCopy.estimate_obj'></stickies>
            <crew-visits class='mt-4' v-if='woCopy' :wo='woCopy' mini></crew-visits>
          </v-col>
          <v-col cols='8'>
            <wo-scheduling-log ref='schedulingLog' v-if='woCopy' :wo='woCopy' mini></wo-scheduling-log>
            <outstanding-service ref='oustandingService' class="mt-4"
              :project='woCopy.project_obj'></outstanding-service>
          </v-col>
        </v-row>
      </div>
      <template v-slot:dialog-actions>
        <v-btn small outlined text @click='openWO'>
          Open WO
          <v-icon right small color='grey darken-2'>mdi-open-in-new</v-icon>
        </v-btn>
        <v-btn small outlined text @click='openProject'>
          Open Project
          <v-icon right small color='grey darken-2'>mdi-open-in-new</v-icon>
        </v-btn>
        <template v-if='!woCopy || !woCopy.park'>
          <v-btn small outlined text @click='parkWO' color='error'>
            Park
            <v-icon right small color='error'>mdi-parking</v-icon>
          </v-btn>
        </template>
        <template v-else>
          <v-btn small outlined text @click='parkWO'>
            UnPark
            <v-icon right small color='grey darken-2'>mdi-parking</v-icon>
          </v-btn>
        </template>
        <v-btn small outlined text color='info' @click='loadPrev'>
          Prev
          <v-icon right small color="info">mdi-arrow-left</v-icon>
        </v-btn>
        <v-btn small outlined text color='info' @click='loadNext'>
          Next
          <v-icon right small color="info">mdi-arrow-right</v-icon>
        </v-btn>

      </template>
    </wc-dialog>

    <wc-confirm :showDialog='showClearAttemptedDialog' saveText="Yes, clear attempted status"
      @cancel='showClearAttemptedDialog = false' @confirm='clearAttempted'>
      <span class='font-weight-bold'>Clear attempted status for this WO?</span><br><br>
    </wc-confirm>

  </div>
</template>

<script>
import moment from 'moment';
import Contacts from '@/modules/irrigation/components/Contacts';
import EstimateContacts from '@/modules/estimates/components/Contacts';
import wosAPI from '@/modules/wos/api';
import irrigationAPI from '@/modules/irrigation/api/irrigations';
import SchedulingLog from '@/modules/wos/views/woDetail/SchedulingLog';
import SchedulingConsiderations from '@/modules/wos/views/woDetail/SchedulingConsiderations';
import CrewVisits from '@/modules/wos/views/woDetail/CrewVisits';
import WOTimeline from '@/modules/wos/components/WOTimeline';
import WCIsCapitalInstall from '@/components/chips/WCIsCapitalInstall';
import Stickies from '@/modules/projects/components/Stickies';
import OutstandingService from "@/modules/projects/components/OutstandingService";
import _ from 'lodash';

export default {
  name: 'wo-quick-view',
  props: {
    wo: {
      required: true
    },
    schedule: {
      required: false
    },
    showDialog: {
      required: true
    }
  },
  data() {
    return {
      isLoading: true,
      schedulingLog: [],
      showLD: false,
      visits: [],
      suNote: [],
      bdNote: [],
      woCopy: null,
      logCopy: {},
      logReset: {
        work_order: null,
        created_by: this.$store.state.auth.profile.id,
        notes: ''
      },
      showClearAttemptedDialog: false
    }
  },
  watch: {
    wo: function (old_wo, new_wo) {
      console.log(old_wo.id, new_wo.id);
      this.getData();
    }
  },
  mounted() {
    this.getData()
  },
  computed: {
    title() {
      if (this.wo && this.wo.project) {
        return this.wo.project_obj.name
      } else if (this.wo && this.wo.estimate) {
        return this.wo.estimate_obj.name
      }
      return ''
    },
    subtitle() {
      if (this.wo && this.wo.project) {
        return this.wo.project_obj.site_address
      } else if (this.wo && this.wo.estimate) {
        return this.wo.estimate_obj.site_address
      }
      return ''
    },
    woType() {
      return this.wo ? this.$store.state.misc.woTypes.find(t => t.id === this.wo.wo_type) : ''
    },
    icons() {
      return this.$store.state.icons
    },
    basicData() {
      return [
        { text: 'Type', value: this.woType.text },
        { text: 'Number', value: this.woCopy.number }
      ]
    },
    year() {
      return parseInt(moment().format('YYYY'))
    },
  },
  methods: {
    async getData() {
      this.isLoading = true;
      this.woCopy = await wosAPI.getQuickViewWO(this.wo.id);
      if (!this.woCopy.is_template) {
        setTimeout(() => {
          this.$refs.schedulingLog.getData();
        }, 100);
      }
      if (this.woCopy.project && (this.woCopy.wo_type_obj.category === 'SU' || this.woCopy.wo_type_obj.category === 'BD')) {
        let notes = await irrigationAPI.getOneTimeNotes(this.woCopy.project_obj.id)
        this.suNote = _.filter(notes, { 'note_year': this.year, 'is_su_note': true, 'note_type': 'F' })
        this.bdNote = _.filter(notes, { 'note_year': this.year, 'is_su_note': false, 'note_type': 'F' })
      }
      this.isLoading = false;
    },
    updateWO(wo) {
      this.woCopy = _.cloneDeep(wo);
      this.$emit('updateQuickView', this.woCopy);
    },
    openWO() {
      const link = document.createElement('a')
      const invURL = this.$router.resolve({ name: 'WOOverview', params: { id: this.woCopy.id } })
      link.target = '_blank'
      link.href = invURL.href
      link.click()
    },
    openProject() {
      const link = document.createElement('a')
      let url = "";
      console.log(JSON.parse(JSON.stringify(this.woCopy)));

      if (this.woCopy.project_type === "L") {
        url = this.$router.resolve({ name: 'LawnOverview', params: { id: this.woCopy.project } })
      }
      else if (this.woCopy.project_type === "I") {
        url = this.$router.resolve({ name: 'IrrigationOverview', params: { id: this.woCopy.project } })
      }
      else if (this.woCopy.project_type === "S") {
        url = this.$router.resolve({ name: 'IrrigationOverview', params: { id: this.woCopy.project } })
      }
      link.target = '_blank'
      link.href = url.href
      link.click()
    },
    async parkWO() {
      const data = {
        "id": this.woCopy.id,
        "park": !this.woCopy.park
      }
      const returned_wo = await wosAPI.updateWO(data);
      this.woCopy = returned_wo;
      this.$emit('updateQuickView', returned_wo);
    },
    close() {
      this.woCopy = null
      this.$emit('close')
    },
    loadNext() {
      this.$emit("loadNext");
    },
    loadPrev() {
      this.$emit("loadPrev");
    },
    confirmClear() {
      this.showClearAttemptedDialog = true
    },
    async clearAttempted() {
      const data = {
        "id": this.woCopy.id,
        "is_attempted": false
      };

      const returned_wo = await wosAPI.updateWO(data);
      this.$emit('updateQuickView', returned_wo);
      this.showClearAttemptedDialog = false;
    }
  },
  components: {
    'wo-scheduling-log': SchedulingLog,
    'wo-scheduling-considerations': SchedulingConsiderations,
    'crew-visits': CrewVisits,
    'outstanding-service': OutstandingService,
    'contacts': Contacts,
    'estimate-contacts': EstimateContacts,
    'wo-timeline': WOTimeline,
    'wc-is-capital-install': WCIsCapitalInstall,
    'stickies': Stickies
  }
}
</script>
